import type { NestedFilter } from '@@partner:graphql/types'

import React, { useState, useCallback } from 'react'
import { useFetchAllMarketingStories } from '@@partner:graphql/query/allStories/allMarketingStories.gql'
import { useGridHelper } from '@@partner:helpers/crud/grid'
import { generatePath } from 'react-router'

import StoriesTable from './grid/StoriesTable'
import StoriesFilter from './grid/StoriesFilter'
import Pagination from 'rsuite/esm/Pagination/Pagination'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'

import { ROUTE_MARKETING_STORIES_EDIT } from '@@partner:config/routes'

export interface StoriesGridProps {
  brandSlug?: string
}

const defaultStoriesFilter: NestedFilter = {
  fields: [ 'id' ],
  op: 'unwrap'
}

export default function StoriesGrid ({ brandSlug }: StoriesGridProps) {
  const [ filter, setFilter ] = useState<NestedFilter>(defaultStoriesFilter)
  const { page, tableSort, handlePageSelect, handleSortColumn } = useGridHelper()

  const { data, loading, error } = useFetchAllMarketingStories({
    filter: filter,
    pager: { page },
    sort: {
      fieldName: tableSort.fieldName,
      direction: tableSort.direction
    }
  })

  const generateEditUrl = useCallback(
    (storyId: string | number) => {
      return generatePath(ROUTE_MARKETING_STORIES_EDIT, {
        brandSlug,
        storyId: String(storyId)
      })
    },
    [ brandSlug ]
  )

  const handleFilterChanged = useCallback(
    (updatedFilter: NestedFilter) => {
      setFilter(updatedFilter)
      handlePageSelect(1)
    },
    [ handlePageSelect ]
  )

  if (error) {
    return (
      <GraphQLCommonErrorDisplay
        title="Ошибка загрузки историй"
        error={error}
      />
    )
  }

  return (
    <div>
      <StoriesFilter onFilterChanged={handleFilterChanged} />

      <StoriesTable
        items={data?.allMarketingStories?.edges ?? []}
        loading={loading}
        sortColumn={tableSort.fieldName}
        sortType={tableSort.sortType}
        onSortColumn={handleSortColumn}
        generateEditUrl={generateEditUrl}
      />

      <Pagination
        last
        first
        next
        prev
        ellipsis
        activePage={page}
        pages={data?.allMarketingStories?.pageInfo?.lastPage}
        maxButtons={10}
        onSelect={handlePageSelect}
      />
    </div>
  )
}
